import EnvironmentVariables from '@/EnvironmentVariables';
import { authService, AuthType } from '../auth-service';
import { getDpopKey } from './dpop-key-store';
import { type AuthorizationHeaders, chooseAuthorizationHeaders } from './dpop-headers';
import { type DpopEnvironment, dpopMode } from './dpop-policy';
import { createDpopProof } from './dpop-proof';
import { getDpopToken, invalidateDpopToken } from './dpop-session';

/**
 * Chooses the authorization headers for one outgoing request: DPoP when every condition below holds,
 * otherwise the Bearer header the portal has always sent. Never throws - any failure means Bearer.
 * The portal's counterpart of Gorelo Web's dpop-authorization.ts, without strict mode.
 *
 * DPoP is used only when:
 *  - the build enables it (VITE_dpopEnabled=true);
 *  - the contact signed in through Azure AD B2C. Magic-link sessions carry gateway-minted tokens the
 *    exchange deliberately refuses;
 *  - the browser can hold a non-extractable key (secure context, WebCrypto, IndexedDB);
 *  - the support kill switch is not set: localStorage['gorelo.dpop.disabled'] = 'true';
 *  - the request is going to the current region's API gateway. The public document function, blob
 *    storage and every other host keep their own credentials and never see a proof or a gateway token.
 */

export type { AuthorizationHeaders } from './dpop-headers';

const KILL_SWITCH_KEY = 'gorelo.dpop.disabled';

/**
 * `isRetry`: the response interceptor is retrying this request after a 401, so the held token is dropped
 * and exchanged again. `isRepeatRetry`: it is retrying it again. The portal's 401 retry has no limit of
 * its own, so a request DPoP has already re-exchanged for goes as Bearer rather than exchanging forever.
 */
export async function buildAuthorizationHeaders(
    method: string | undefined,
    resolveUrl: () => string,
    b2cAccessToken: string | null,
    isRetry: boolean,
    isRepeatRetry: boolean,
): Promise<AuthorizationHeaders> {
    const env = currentEnvironment();
    return chooseAuthorizationHeaders({
        method,
        resolveUrl,
        b2cAccessToken,
        isRetry,
        mode: isRepeatRetry ? 'off' : dpopMode(env),
        strict: false,
        gatewayOriginOf,
        invalidateToken: invalidateDpopToken,
        getToken: (origin, b2c) => getDpopToken(origin, b2c, () => authService.getFreshAccessToken()),
        createProof: async (m, url, token) => createDpopProof(await getDpopKey(), m, url, token),
    });
}

function currentEnvironment(): DpopEnvironment {
    if (import.meta.env.VITE_dpopEnabled !== 'true') {
        // Nothing else is evaluated with DPoP off.
        return { enabled: false, strict: false, b2cSession: false, capable: false, killSwitch: false };
    }
    let killSwitch = false;
    try {
        killSwitch = localStorage.getItem(KILL_SWITCH_KEY) === 'true';
    } catch {
        // Storage unreadable: nothing set the kill switch.
    }
    return {
        enabled: true,
        strict: false,
        b2cSession: authService.getAuthType() === AuthType.AzureAD,
        capable: typeof window !== 'undefined' && window.isSecureContext
            && !!globalThis.crypto?.subtle && typeof indexedDB !== 'undefined',
        killSwitch,
    };
}

/** The gateway origin when `url` targets the current region's gateway, else null. */
export function gatewayOriginOf(url: string): string | null {
    const apimUrl = EnvironmentVariables.getApimUrl();
    if (!apimUrl) return null;
    try {
        const gateway = new URL(apimUrl).origin;
        return new URL(url, window.location.origin).origin === gateway ? gateway : null;
    } catch {
        return null;
    }
}
